import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiArrowDown, FiRefreshCw, FiZap } = FiIcons;

const Problem = () => {
  const painPoints = [
    "You wake up every morning promising \"today will be different\"—but by evening, you're back where you started",
    "That split second between craving and using feels like it happens before you can even think",
    "You know exactly what you should do, but in the moment, your brain just...doesn't cooperate",
    "Everyone says \"just don't pick up\" like it's that simple—but they don't understand the mental chaos",
    "You've been clean for weeks or months, then one stressful day destroys everything",
    "Planning ahead feels impossible when you can barely get through the next hour",
    "Other people seem to handle stress, boredom, and emotions without needing something to take the edge off",
    "You're tired of disappointing the people who believe in you—again"
  ];

  const addictionCycle = [
    {
      title: "The Spiral",
      description: "A small stressor leads to overwhelming emotion, which leads to impulsive action. The cycle feels impossible to break.",
      icon: FiArrowDown,
      color: "from-red-500 to-pink-500"
    },
    {
      title: "\"F* It\" Mode",
      description: "One small misstep makes the whole day feel like a write-off, leading to a major relapse.",
      icon: FiZap,
      color: "from-orange-500 to-red-500"
    },
    {
      title: "Anxious Planning",
      description: "The constant mental exhaustion of trying to \"white-knuckle\" your way through every day without a drink or drug.",
      icon: FiRefreshCw,
      color: "from-yellow-500 to-orange-500"
    },
    {
      title: "Future Blindness",
      description: "An inability to visualize or plan for a fulfilling future, making sobriety feel empty and pointless.",
      icon: FiCheck,
      color: "from-purple-500 to-pink-500"
    }
  ];

  const keyFacts = [
    "51% of patients with SUD relapsed following discharge from residential treatment",
    "40–80% of patients receiving treatment for alcohol use disorders have at least one drink within the first year after treatment",
    "Executive function deficits predict substance use problems and make recovery significantly harder"
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            When Willpower Isn't Enough
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The Hidden Brain Battle Behind Addiction
          </p>
        </motion.div>

        {/* Pain Points */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Sound familiar?
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex-shrink-0 w-6 h-6 bg-accent-magenta/10 rounded-full flex items-center justify-center mt-1">
                  <div className="w-2 h-2 bg-accent-magenta rounded-full"></div>
                </div>
                <p className="text-gray-700 leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-accent-light/30 rounded-3xl p-8 mb-16"
        >
          <div className="text-center mb-8">
            <p className="text-lg text-gray-700 mb-4">
              The truth is, <strong>16.8 percent of the U.S. population aged 12 or older</strong> (about 48.4 million individuals) met the criteria for a substance use disorder.<sup>1</sup> And long-lasting changes in brain networks involved in reward, executive function, stress reactivity, mood, and self-awareness underlie the intense drive to consume substances.
            </p>
            <p className="text-xl font-semibold text-accent-magenta">
              This isn't a moral failing. It's a brain that needs specific skills to function differently.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {keyFacts.map((fact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm text-center"
              >
                <SafeIcon icon={FiCheck} className="w-8 h-8 text-accent-magenta mx-auto mb-4" />
                <p className="text-gray-700 text-sm leading-relaxed">{fact}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Addiction Cycle */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-8 text-center">
            The Addiction Cycle
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addictionCycle.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                  <SafeIcon icon={item.icon} className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Research Conclusion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-accent-magenta to-accent-purple rounded-3xl p-8 text-center text-white"
        >
          <h3 className="text-2xl font-playfair font-bold mb-4">
            The Neurological Gap We Fill
          </h3>
          <p className="text-lg leading-relaxed">
            Research shows that substance use directly impairs the prefrontal cortex, weakening the very skills needed to maintain recovery: inhibition, emotional regulation, and decision-making. This is the neurological gap we fill.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;